/**
 * 案件ステータス / 進捗ステータスのラベル + fallback helper (PR-18 / Group F)
 *
 * 背景: Dev UI audit F-12 で `/cases` 一覧の「ステータス」列が空欄 `""`
 * になっている行が複数件あった。原因は `STATUS_LABELS[c.status] ?? c.status`
 * 経由で、`c.status` が空文字 / undefined / unknown enum の場合に React が
 * `undefined` を render → 表示が空のセルになる。
 *
 * 対策方針:
 *   - 各 page で重複定義していた STATUS_LABELS / PROGRESS_LABELS を
 *     本 module に集約 (single source of truth)
 *   - 空文字 / null / undefined / unknown 値は自然な日本語 fallback (`未設定`)
 *     に揃えて raw enum / 空セルを UI に晒さない
 *   - DB 値 / API response shape は変更しない (FE 側 fallback のみ)
 *
 * 参照: PR-13 Group A の `caseTypeLabel` と同じ設計パターン
 */
import type { Case, BadgeVariant } from '@/types';

/** 案件ステータス未設定時の fallback (PR-18) */
export const CASE_STATUS_UNKNOWN_FALLBACK = '未設定';

/** 案件 status canonical key → 日本語ラベル */
export const CASE_STATUS_LABELS: Record<Case['status'], string> = {
  active: '進行中',
  completed: '完了',
  resigned: '辞任',
  deleted: '削除済み',
};

/** 案件 status → Badge variant */
export const CASE_STATUS_BADGE: Record<Case['status'], BadgeVariant> = {
  active: 'blue',
  completed: 'green',
  resigned: 'red',
  deleted: 'gray',
};

/** 進捗ステータス canonical key → 日本語ラベル */
export const PROGRESS_STATUS_LABELS: Record<string, string> = {
  collecting: '収集中',
  reviewing: '確認中',
  completed: '完了',
  resigned: '辞任',
};

/** 進捗ステータス → Badge variant */
export const PROGRESS_STATUS_BADGE: Record<string, BadgeVariant> = {
  collecting: 'yellow',
  reviewing: 'cyan',
  completed: 'green',
  resigned: 'red',
};

/**
 * 案件ステータス値を日本語ラベルに解決する共通 formatter (PR-18)。
 *
 * 解決順:
 *   1. CASE_STATUS_LABELS に直接ヒット (canonical) → ラベル
 *   2. Japanese 値 (`相続` 同様、DB に Japanese 文字列が直接保存されている
 *      レガシーケース) と一致 → そのまま返す
 *   3. null / undefined / 空文字 / unknown 文字列 → fallback (`未設定` 既定)
 *
 * `STATUS_LABELS[c.status] ?? c.status` の旧パターンは空文字 c.status を
 * そのまま render してしまうため、本 helper を経由することで UI に空セルや
 * raw enum が出ない。
 */
export function caseStatusLabel(
  value: string | null | undefined,
  fallback: string = CASE_STATUS_UNKNOWN_FALLBACK,
): string {
  if (!value) return fallback;
  // 1. canonical
  const direct = CASE_STATUS_LABELS[value as Case['status']];
  if (direct) return direct;
  // 2. label-set に既に存在する Japanese 値 → そのまま (defensive、現状の dev
  //    data には該当しないが将来の DB 直接編集を許容)
  for (const label of Object.values(CASE_STATUS_LABELS)) {
    if (label === value) return label;
  }
  // 3. unknown → fallback (raw enum を UI に晒さない)
  return fallback;
}

/**
 * 進捗ステータス値を日本語ラベルに解決する共通 formatter (PR-18)。
 *
 * `caseStatusLabel` と同じセマンティクスで PROGRESS_STATUS_LABELS を引く。
 * 案件詳細 / 一覧の progressStatus 列に空文字を出さないため使う。
 */
export function progressStatusLabel(
  value: string | null | undefined,
  fallback: string = CASE_STATUS_UNKNOWN_FALLBACK,
): string {
  if (!value) return fallback;
  const direct = PROGRESS_STATUS_LABELS[value];
  if (direct) return direct;
  for (const label of Object.values(PROGRESS_STATUS_LABELS)) {
    if (label === value) return label;
  }
  return fallback;
}

/**
 * Badge variant を解決する helper (PR-18)。
 *
 * 値が unknown のときは `gray` を返す (旧 `STATUS_BADGE[row.status]` で
 * undefined が出ていたパスを明示的に拾う)。
 */
export function caseStatusBadgeVariant(value: string | null | undefined): BadgeVariant {
  if (!value) return 'gray';
  return CASE_STATUS_BADGE[value as Case['status']] ?? 'gray';
}

export function progressStatusBadgeVariant(value: string | null | undefined): BadgeVariant {
  if (!value) return 'gray';
  return PROGRESS_STATUS_BADGE[value] ?? 'gray';
}

// ==============================
// 申立 (lifecycle milestone) — steering 471
//
// 進捗ステータス (収集中 / 確認中 / 完了) が「書類回収のボールの所在」を表すのに対し、
// 申立済は「裁判所へ申立てたか」という **別軸の lifecycle marker**。両者は併存して表示し、
// 申立済を表すために進捗ステータスの値域を増やさない (ADR-003 / ubiquitous-language §4.1.1)。
// ==============================

/** 申立済バッジのラベル */
export const FILING_STATUS_LABEL = '申立済';

/** 申立済バッジの Badge variant (進捗ステータスの色と重ならない purple を使う) */
export const FILING_STATUS_BADGE: BadgeVariant = 'purple';

/**
 * 再申立バッジのラベル (case-refiling-date)。裁判所から差し戻され申立し直した案件に、
 * 申立済バッジ (元の申立日) の隣へ日付つきで並べる。色は申立済と同じ purple。
 */
export const REFILING_STATUS_LABEL = '再申立';

/**
 * 申立済かどうかを `filingDate` の有無から導出する。
 *
 * DB に別ステータスを持たず、**申立日が入っていること = 申立済**とする派生判定。
 * 空文字は「未設定」として扱う (legacy レコードは属性自体を持たない)。
 */
export function isCaseFiled(filingDate: string | null | undefined): boolean {
  return typeof filingDate === 'string' && filingDate.trim() !== '';
}

/**
 * 申立日 (`YYYY-MM-DD`) を日本語表示へ整形する。未申立 / 不正値は `null`。
 *
 * date-only 値をそのまま `new Date()` に渡すと UTC 解釈されて JST で前日になるため、
 * 文字列を分解して組み立てる (表示だけの整形で、保存値は変えない)。
 */
export function filingDateLabel(filingDate: string | null | undefined): string | null {
  if (!isCaseFiled(filingDate)) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((filingDate as string).trim());
  if (!m) return null;
  return `${Number(m[1])}年${Number(m[2])}月${Number(m[3])}日`;
}
